import axios from 'axios';
import { openDB } from 'idb';

import Log from 'utils/Log';
import Settings from 'utils/Settings';

import { stringifyQueryString } from '../../utils/Url';

export const TEMPLATES = {
  DEFAULT: 'json',
  MASTER_PRODUCT: 'master-product',
};

const dbPromise = openDB('CollectionDB', 1, {
  upgrade(db) {
    db.createObjectStore('collections', { keyPath: 'id' });
  },
});

const CollectionAPI = class {
  static async cacheCollection({ cacheKey, data }) {
    const db = await dbPromise;
    await db.put('collections', {
      id: cacheKey,
      data,
      timestamp: new Date().getTime(),
      version: Settings.masterProductCacheVersion,
    });
  }

  static async fetchCachedCollection(cacheKey) {
    try {
      const db = await dbPromise;
      const cachedCollection = await db.get('collections', cacheKey);
      const now = new Date().getTime();
      const cacheTTL = Number(Settings.masterProductCacheTTL) * 60_000;
      // Check if cached data exists and it's not older than the cache TTL setting
      // and if the cached version matches the current settings_schema.json version
      if (
        cachedCollection &&
        now - cachedCollection.timestamp < cacheTTL &&
        cachedCollection.version === Settings.masterProductCacheVersion
      ) {
        return cachedCollection.data;
      }

      // If the version doesn't match or data is stale, return null to fetch new data
      return null;
    } catch (error) {
      Log.debug(`fetchCachedCollection: Could not fetch cacheKey: ${cacheKey}`);
      return null;
    }
  }

  static fetchCollection = async ({
    handle,
    sortOrder = undefined,
    template = TEMPLATES.DEFAULT,
  }) => {
    const cacheKey = `collection_${handle}_${template}`;
    const cached = await CollectionAPI.fetchCachedCollection(cacheKey);

    if (Settings.masterProductCacheEnabled && cached) {
      return Promise.resolve(cached);
    }

    const params = stringifyQueryString({
      view: template,
      sort_by: sortOrder,
    });

    return axios.get(`/collections/${handle}?${params}`).then(async ({ data }) => {
      if (Settings.masterProductCacheEnabled) {
        await CollectionAPI.cacheCollection({ cacheKey, data });
      }

      return data;
    });
  };

  static fetchUpsells = () =>
    CollectionAPI.fetchCollection({ handle: 'upsells', template: TEMPLATES.DEFAULT });

  static fetchMasterProduct = handle =>
    CollectionAPI.fetchCollection({
      handle,
      template: TEMPLATES.MASTER_PRODUCT,
    });
};

export default CollectionAPI;
